import React, { useEffect, useState, useCallback, useMemo, useContext, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Pencil, Building2, ExternalLink, Clock, ClipboardList, Send, Users,
  CalendarCheck, CheckCircle2, UserX, Ban, History, Percent, BellRing, UserPlus,
} from 'lucide-react';

import { toast } from 'react-toastify';

import { Button, Card } from '../../ui-v2';
import {
  getResumenCampana, deleteCampana,
  cambiarEstadoCampana, contarParticipacionesCanceladasSistema,
  descargarCsvResumen, registrarIntervencion, deshacerIntervencion, getCandidatosIntervencion,
  reabrirCita,
} from '../api';
import RegistrarIntervencionModal from '../compartidos/RegistrarIntervencionModal';
import ModalCancelarCita from '../compartidos/ModalCancelarCita';
import { Modal, Badge, Tabs, Tab, KpiCard, ProgressBar } from '../../ui-v2.5';
import AccionesCampanaCancelada from '../campanas/AccionesCampanaCancelada';
import { UsuarioContext } from '../../../providers/personaContext';
import PanelLotes from '../lotes/PanelLotes';
import FiltrosResumen, { BotonCsv, KpisPersonas } from '../lotes/FiltrosResumen';
import { ESTADO_CAMPANA, etiquetaDe } from '../compartidos/etiquetasEstado';

import MiniDonut from '../resumen/MiniDonut';
import SeccionCitas from '../resumen/SeccionCitas';
import SeccionUsuarios from '../resumen/SeccionUsuarios';
import SeccionTecnicos from '../resumen/SeccionTecnicos';
import {
  leerPestana, paramsDePestana, filtrarCitas, contarCitas, avisosResumen, permisosResumen,
} from '../resumen/resumenPestanas';

/**
 * ResumenCampana — rediseño (Fase 1, 4/11): pestañas Resumen · Personas · Citas · Técnicos.
 *
 *   · La pestaña y su filtro van en la URL (`?tab=` y `?filtro=`): enlazables y recargables. Sin
 *     `?tab=` se abre Resumen (resumenPestanas.leerPestana).
 *   · Los filtros del servidor (buscador, lote, empresa, negocio, departamento, estado) son una barra
 *     COMÚN encima de las pestañas, como antes: afectan a todo lo de debajo (cifras, donut, personas,
 *     citas, técnicos) y al CSV. Los lotes, no: son la vista de toda la campaña.
 *   · Solo se enseña lo que se puede hacer (resumenPestanas.permisosResumen); la barrera es el servidor.
 *   · La API, los modales y sus manejadores son los de antes, sin cambios.
 *   · Todo el ancho disponible (sin max-w).
 *
 * z-index sticky: la barra global es z-[1050] top-0 (~44px); las pestañas van en top-14 (56px), z-20.
 */

export default function ResumenCampana() {
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const id = parseInt(idParam, 10);
  const [sp, setSp] = useSearchParams();
  const { tab, filtro } = leerPestana(sp);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filtroTecnico, setFiltroTecnico] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null);

  // Lotes · filtros del resumen. Se aplican en el SERVIDOR (lista, métricas y KPI), con la misma
  // función que el CSV. Al cambiarlos se recarga sin vaciar la pantalla.
  const [filtros, setFiltros] = useState({});
  const [actualizando, setActualizando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [versionLotes, setVersionLotes] = useState(0);
  const [lotes, setLotes] = useState(null); // los de PanelLotes, para «lotes sin avisar»
  const hayDatos = useRef(false);
  // Intervenciones sin cita (24/10): a quién se registra, y a quién se le deshace.
  const [registrarDe, setRegistrarDe] = useState(null);
  const [deshacerDe, setDeshacerDe] = useState(null);
  const [deshaciendo, setDeshaciendo] = useState(false);
  // 3/11 · reabrir una cita realizada (gestión).
  const [reabrirDe, setReabrirDe] = useState(null);
  const [reabriendo, setReabriendo] = useState(false);
  const [altaAbierta, setAltaAbierta] = useState(false);

  const { persona, tienePermiso } = useContext(UsuarioContext);
  const permisos = permisosResumen({ rol: persona?.rol, tienePermiso, estado: data?.campana?.estado });

  const cargar = useCallback(async () => {
    setActualizando(true);
    if (!hayDatos.current) setError(null);
    try {
      const res = await getResumenCampana(id, filtros);
      setData(res);
      hayDatos.current = true;
    } catch (e) {
      console.error('[ResumenCampana]', e);
      const msg = e?.response?.status === 404
        ? 'La campaña no existe.'
        : (e?.response?.data?.error || 'No se pudo cargar el resumen.');
      // Con datos en pantalla, un fallo al filtrar no la tira: se avisa y se queda lo que había.
      if (hayDatos.current) toast.error(msg); else setError(msg);
    } finally { setLoading(false); setActualizando(false); }
  }, [id, filtros]);

  useEffect(() => { if (id) cargar(); }, [id, cargar]);

  // Ir a una pestaña (con su filtro): nueva entrada en el historial, arriba del todo.
  const ir = useCallback((t, f = null) => {
    setSp(paramsDePestana(sp, t, f));
    window.scrollTo(0, 0);
  }, [sp, setSp]);
  // Cambiar el filtro dentro de la pestaña: la URL lo lleva, sin llenar el historial.
  const cambiarFiltro = useCallback((f) => setSp(paramsDePestana(sp, tab, f), { replace: true }), [sp, setSp, tab]);
  const alCargarLotes = useCallback((l) => setLotes(l), []);

  const citasTodas = useMemo(() => data?.citas || [], [data]);
  const conteosCitas = useMemo(() => contarCitas(citasTodas), [citasTodas]);
  const citasFiltradas = useMemo(
    () => filtrarCitas(citasTodas, { estado: tab === 'citas' ? filtro : 'todas', tecnico: filtroTecnico }),
    [citasTodas, tab, filtro, filtroTecnico],
  );
  const avisos = useMemo(
    () => (data ? avisosResumen({ campana: data.campana, usuarios: data.usuarios, citas: data.citas, lotes }) : []),
    [data, lotes],
  );

  async function exportarCsv() {
    setExportando(true);
    try {
      await descargarCsvResumen(id, filtros);
    } catch (e) {
      toast.error('No se pudo exportar el CSV.');
    } finally { setExportando(false); }
  }

  if (loading) {
    return (
      <div className="px-6 py-16 text-center text-sm text-slate-500">
        <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" />
        Cargando resumen…
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-6 py-12 max-w-xl mx-auto">
        <div className="border border-red-200 bg-red-50 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
        <div className="mt-4">
          <Button variant="outline" onClick={() => navigate('/citas-tecnicas/campanas')}>
            <ArrowLeft className="w-4 h-4" />
            Volver a la lista
          </Button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { campana, metricas, usuarios, tecnicos } = data;

  async function confirmarDeshacer() {
    setDeshaciendo(true);
    try {
      await deshacerIntervencion(campana.id, deshacerDe.id);
      toast.success('Intervención deshecha: vuelve a como estaba.');
      setDeshacerDe(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se ha podido deshacer.');
    } finally {
      setDeshaciendo(false);
    }
  }
  async function confirmarReabrir() {
    setReabriendo(true);
    try {
      await reabrirCita(reabrirDe.id);
      toast.success('Cita reabierta: vuelve a reservada.');
      setReabrirDe(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se ha podido reabrir la cita.');
    } finally {
      setReabriendo(false);
    }
  }
  // La cita viva de la persona, si la tiene: el modal ofrece marcar ESA en vez de registrar otra.
  const citaVivaDe = (u) => (u && u.cita && ['reservada', 'confirmada'].includes(u.cita.estado) ? u.cita : null);
  const estado = etiquetaDe(ESTADO_CAMPANA, campana.estado);
  const colab = campana.ambito === 'colaborador';

  return (
    <div className="min-h-full bg-v25-surface-sunken">
      {/* CABECERA: volver, título, estado, ámbito, fechas y acciones; debajo, los filtros comunes */}
      <div className="border-b border-v25-line bg-white">
        <div className="px-4 pt-4 sm:px-6">
          <button
            type="button"
            onClick={() => navigate('/citas-tecnicas/campanas')}
            className="mb-2 inline-flex items-center gap-1 text-xs text-v25-ink-500 hover:text-v25-ink-900"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Campañas
          </button>
          <div className="flex flex-col gap-3 pb-4 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-semibold text-v25-ink-900">{campana.titulo}</h1>
                <Badge tone={estado.tone}>{estado.label}</Badge>
                <Badge tone="info" icon={colab ? ExternalLink : Building2}>{colab ? 'Colaboradores' : 'Internos'}</Badge>
              </div>
              <p className="mt-1 text-sm text-v25-ink-500">
                {/* Sin horario: horario_inicio–horario_fin no corresponde a las franjas reales; vuelve con el planificador (C). */}
                Hasta el {formatFecha(campana.fecha_limite)} · citas de {campana.duracion_cita} min
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {permisos.editar && (
                <Button variant="outline" size="sm" onClick={() => navigate(`/citas-tecnicas/campanas/${campana.id}/editar`)}>
                  <Pencil className="h-4 w-4" /> Editar campaña
                </Button>
              )}
              {/* Fase 13 (Opción C) — solo con la campaña Cancelada; cada botón, con su permiso */}
              <AccionesCampanaCancelada
                campana={campana}
                puedeReactivar={permisos.reactivar}
                puedeEliminar={permisos.eliminar}
                onEliminada={() => navigate('/citas-tecnicas/campanas')}
                onReactivada={(resp) => {
                  let msg = 'Campaña reactivada.';
                  if (resp?.participaciones_revividas > 0) {
                    msg += ` Se restauraron ${resp.participaciones_revividas} participaciones.`;
                  }
                  if (resp?.aviso) msg += ` ${resp.aviso}`;
                  toast.success(msg);
                  cargar();
                }}
                eliminarCampana={deleteCampana}
                cambiarEstadoCampana={cambiarEstadoCampana}
                contarParticipacionesCanceladasSistema={contarParticipacionesCanceladasSistema}
              />
            </div>
          </div>
          {/* Filtros COMUNES a todas las pestañas (y al CSV), como antes */}
          <FiltrosResumen
            ambito={campana.ambito}
            disponibles={data.filtros_disponibles}
            filtros={filtros}
            onChange={setFiltros}
            kpis={data.kpis}
            actualizando={actualizando}
            conKpis={false}
          />
        </div>
      </div>

      {/* PESTAÑAS, fijas bajo la barra superior */}
      <div className="sticky top-14 z-20 border-b border-v25-line bg-white">
        <div className="overflow-x-auto px-4 sm:px-6">
          <Tabs value={tab} onChange={(t) => ir(t)}>
            <Tab value="resumen" label="Resumen" />
            <Tab value="personas" label="Personas" count={usuarios.length} />
            <Tab value="citas" label="Citas" count={citasTodas.length} />
            <Tab value="tecnicos" label="Técnicos" count={tecnicos.length} />
          </Tabs>
        </div>
      </div>

      <div className="px-4 py-6 sm:px-6">
        {tab === 'resumen' && (
          <PestanaResumen
            campana={campana}
            metricas={metricas}
            kpis={data.kpis}
            filtros={filtros}
            avisos={avisos}
            ir={ir}
          />
        )}

        {/* Personas. PanelLotes se queda montado (oculto fuera de esta pestaña): así los lotes se
            cargan una vez y el aviso «lotes sin avisar» de Resumen los tiene sin otra llamada. */}
        <div className={tab === 'personas' ? 'space-y-4' : 'hidden'}>
          <PanelLotes
            campana={campana}
            puedeGestionar={permisos.gestionar}
            version={versionLotes}
            onCambio={() => { setVersionLotes((v) => v + 1); cargar(); }}
            onLotes={alCargarLotes}
          />
          <div className="flex flex-wrap items-center justify-end gap-2">
            {permisos.altaDeFuera && (
              <Button size="sm" variant="outline" onClick={() => setAltaAbierta(true)}>
                <UserPlus className="h-4 w-4" /> Registrar intervención a alguien de fuera
              </Button>
            )}
            {permisos.exportar && <BotonCsv onExportar={exportarCsv} exportando={exportando} />}
          </div>
          {tab === 'personas' && (
            <SeccionUsuarios
              usuarios={usuarios}
              puedeGestionar={permisos.registrar}
              onRegistrar={setRegistrarDe}
              onDeshacer={setDeshacerDe}
              filtro={filtro}
              onFiltro={cambiarFiltro}
            />
          )}
        </div>

        {tab === 'citas' && (
          <SeccionCitas
            citas={citasFiltradas}
            totalCitas={citasTodas.length}
            tecnicos={tecnicos}
            filtroEstado={filtro}
            setFiltroEstado={cambiarFiltro}
            filtroTecnico={filtroTecnico}
            setFiltroTecnico={setFiltroTecnico}
            conteos={conteosCitas}
            onCancelar={permisos.cancelarCita ? setCancelTarget : null}
            onReabrir={permisos.reabrirCita ? setReabrirDe : null}
          />
        )}

        {tab === 'tecnicos' && <SeccionTecnicos tecnicos={tecnicos} />}
      </div>

      <RegistrarIntervencionModal
        open={!!registrarDe}
        subtareas={data.subtareas || []}
        onClose={() => setRegistrarDe(null)}
        persona={registrarDe ? { id: registrarDe.id, nombre: registrarDe.nombre_completo } : null}
        tecnicos={tecnicos}
        citaViva={citaVivaDe(registrarDe)}
        registrar={(datos, pid) => registrarIntervencion(campana.id, pid, datos)}
        onHecho={() => { toast.success('Intervención registrada.'); cargar(); }}
      />
      {/* Alta: alguien que aún no está en la campaña (se añade a un lote y se registra, todo junto). */}
      <RegistrarIntervencionModal
        open={altaAbierta}
        subtareas={data.subtareas || []}
        onClose={() => setAltaAbierta(false)}
        tecnicos={tecnicos}
        lotes={data.filtros_disponibles?.lotes || []}
        buscarCandidatos={(q) => getCandidatosIntervencion(campana.id, q)}
        registrar={(datos, pid) => registrarIntervencion(campana.id, pid, datos)}
        onHecho={() => { toast.success('Añadida a la campaña y con la intervención registrada.'); setVersionLotes((v) => v + 1); cargar(); }}
      />
      <Modal
        open={!!deshacerDe}
        onClose={deshaciendo ? undefined : () => setDeshacerDe(null)}
        size="sm"
        tone="danger"
        title="Deshacer la intervención"
        subtitle={deshacerDe?.nombre_completo || ''}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDeshacerDe(null)} disabled={deshaciendo}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmarDeshacer} disabled={deshaciendo}>
              {deshaciendo ? 'Deshaciendo…' : 'Deshacer'}
            </Button>
          </>
        )}
      >
        <p className="text-sm text-slate-700">
          Vuelve a como estaba antes de registrarla: si se registró sin cita, se borra; si se marcó su
          cita, la cita vuelve a estar reservada. Queda en el historial quién lo deshizo y cuándo.
        </p>
      </Modal>
      <Modal
        open={!!reabrirDe}
        onClose={reabriendo ? undefined : () => setReabrirDe(null)}
        size="sm"
        title="Reabrir la cita"
        subtitle={reabrirDe?.usuario?.nombre_completo || ''}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setReabrirDe(null)} disabled={reabriendo}>Cancelar</Button>
            <Button onClick={confirmarReabrir} disabled={reabriendo}>{reabriendo ? 'Reabriendo…' : 'Reabrir'}</Button>
          </>
        )}
      >
        <p className="text-sm text-slate-700">
          Vuelve a reservada y la persona, a «cita reservada». Las subtareas marcadas se conservan y
          queda en el historial quién la reabrió. Si la cita aún no ha pasado, se le vuelven a programar
          los recordatorios.
        </p>
      </Modal>

      <ModalCancelarCita
        cita={cancelTarget}
        persona={cancelTarget?.usuario?.nombre_completo}
        onClose={() => setCancelTarget(null)}
        onHecho={async () => {
          setCancelTarget(null);
          toast.success('Cita cancelada. Se ha avisado a la persona para que reserve otra.');
          await cargar();
        }}
      />
    </div>
  );
}

// ── Pestaña Resumen ──────────────────────────────────────────────────────────────────────────
const DONUT = (m) => [
  { label: 'Completados', value: m.usuarios_realizado, color: '#003c66' },
  { label: 'Con cita', value: m.usuarios_con_cita, color: '#10b981' },
  { label: 'Pendientes', value: m.usuarios_pendientes, color: '#fbbf24' },
  { label: 'Cancelados', value: m.usuarios_cancelado, color: '#cbd5e1' },
];

function PestanaResumen({ campana, metricas: m, kpis, filtros, avisos, ir }) {
  const donut = DONUT(m);
  const sinNoShow = m.tasa_no_show === null || m.tasa_no_show === undefined;
  const porTipo = m.recordatorios_enviados_por_tipo;
  return (
    <div className="grid gap-6 xl:grid-cols-3">
      <div className="min-w-0 space-y-6 xl:col-span-2">
        {/* Progreso + donut */}
        <Card className="p-5">
          <div className="grid items-center gap-6 md:grid-cols-[minmax(0,1fr)_auto]">
            <div className="min-w-0">
              <div className="text-xs font-medium uppercase tracking-wide text-v25-ink-500">Progreso de la campaña</div>
              <div className="mt-2 flex flex-wrap items-baseline gap-3">
                <span className="text-4xl font-semibold tabular-nums text-v25-brand-800">{m.progreso_pct}%</span>
                <span className="text-sm text-v25-ink-500">{m.usuarios_realizado} de {m.usuarios_total} personas completadas</span>
              </div>
              <div className="mt-4"><ProgressBar value={m.progreso_pct} max={100} tone="brand" /></div>
            </div>
            <div className="flex items-center gap-4 md:flex-col md:gap-2">
              <MiniDonut data={donut} size={150} centerLabel={`${m.progreso_pct}%`} centerSubLabel="Completado" />
              <div className="grid grid-cols-1 gap-y-1 text-[11px] md:grid-cols-2 md:gap-x-3">
                {donut.map((d) => (
                  <div key={d.label} className="flex items-center gap-1.5">
                    <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: d.color }} />
                    <span className="text-v25-ink-700">{d.label}</span>
                    <span className="ml-auto tabular-nums text-v25-ink-500">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Fila de PERSONAS (los KPI de los filtros) */}
        <section>
          <h2 className="mb-2 text-sm font-semibold text-v25-ink-900">Personas</h2>
          <KpisPersonas
            kpis={kpis}
            filtros={filtros}
            alPulsar={(f) => ir('personas', f)}
            destinos={{ total: 'todos', sin_cita: 'sin_cita', cancelados: 'cancelados' }}
          />
        </section>

        {/* Fila de CITAS (todas las métricas de antes) */}
        <section>
          <h2 className="mb-2 text-sm font-semibold text-v25-ink-900">Citas</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-7">
            <KpiCard icon={CalendarCheck} label="Reservadas" value={m.citas_reservadas + m.citas_confirmadas} tone="progress"
              note={m.citas_confirmadas > 0 ? `${m.citas_confirmadas} confirmadas` : null} onClick={() => ir('citas', 'abiertas')} />
            <KpiCard icon={CheckCircle2} label="Realizadas" value={m.citas_realizadas} tone="active" onClick={() => ir('citas', 'realizadas')} />
            <KpiCard icon={UserX} label="No asistió" value={m.citas_no_asistio} tone="waiting" onClick={() => ir('citas', 'no_asistio')} />
            <KpiCard icon={Ban} label="Canceladas" value={m.citas_canceladas} onClick={() => ir('citas', 'canceladas')} />
            <KpiCard icon={History} label="Cancelaciones acumuladas" value={m.cancelaciones_acumuladas ?? 0} note="Histórico (incluye no-shows)" />
            <KpiCard icon={Percent} label="Tasa de no-show" value={sinNoShow ? '—' : `${m.tasa_no_show}%`}
              note={sinNoShow ? 'Sin citas cerradas' : 'Sobre citas cerradas'} />
            <KpiCard icon={BellRing} label="Recordatorios enviados" value={m.recordatorios_enviados_total ?? 0}
              note={porTipo ? `${porTipo.RECORDATORIO_1 ?? 0} de 1º · ${porTipo.RECORDATORIO_2 ?? 0} de 2º` : null} />
          </div>
        </section>

        {/* Detalles */}
        <Card className="p-5">
          <h2 className="mb-3 text-sm font-semibold text-v25-ink-900">Detalles</h2>
          <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            {campana.descripcion && <Dato t="Descripción" v={campana.descripcion} ancho />}
            <Dato t="Ámbito" v={campana.ambito === 'colaborador' ? 'Colaboradores' : 'Internos'} />
            <Dato t="Fecha límite" v={formatFecha(campana.fecha_limite)} />
            <Dato t="Duración de cada cita" v={`${campana.duracion_cita} min`} />
          </dl>
        </Card>
      </div>

      {/* Requiere atención */}
      <Card className="h-fit p-5">
        <h2 className="mb-1 text-sm font-semibold text-v25-ink-900">Requiere atención</h2>
        <p className="mb-4 text-xs text-v25-ink-500">Cada aviso lleva a su pestaña, ya filtrada.</p>
        {avisos.length === 0 ? (
          <p className="text-sm text-v25-ink-500">Nada pendiente con los filtros de arriba.</p>
        ) : (
          <ul className="space-y-2">
            {avisos.map((a) => <Aviso key={a.clave} aviso={a} onClick={() => ir(a.tab, a.filtro)} />)}
          </ul>
        )}
      </Card>
    </div>
  );
}

function Dato({ t, v, ancho = false }) {
  return (
    <div className={ancho ? 'sm:col-span-2' : ''}>
      <dt className="text-xs font-medium uppercase tracking-wide text-v25-ink-500">{t}</dt>
      {/* Texto que escribe un usuario: siempre como texto, nunca como HTML. */}
      <dd className="mt-0.5 whitespace-pre-line text-v25-ink-900">{v}</dd>
    </div>
  );
}

const TONO_AVISO = {
  expired: 'border-v25-expired-bd bg-v25-expired-bg text-v25-expired-fg',
  waiting: 'border-v25-waiting-bd bg-v25-waiting-bg text-v25-waiting-fg',
  info: 'border-v25-line bg-white text-v25-ink-700',
};
const ICONO_AVISO = { sin_cerrar: Clock, con_pendientes: ClipboardList, lotes_sin_avisar: Send, sin_cita: Users };

function Aviso({ aviso, onClick }) {
  const Icono = ICONO_AVISO[aviso.clave] || Users;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={`flex w-full items-center gap-3 rounded-v25-md border px-3 py-2.5 text-left text-sm transition-colors hover:brightness-95 ${TONO_AVISO[aviso.tono] || TONO_AVISO.info}`}
      >
        <Icono className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1 font-medium">{aviso.texto}</span>
        <span className="shrink-0 text-xs underline underline-offset-2">{aviso.accion}</span>
      </button>
    </li>
  );
}

function formatFecha(s) {
  if (!s) return '—';
  return new Date(String(s).slice(0, 10) + 'T00:00:00').toLocaleDateString('es-ES', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}
